import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Wordmark from "../components/Wordmark";
import { usePageTitle } from "../hooks/usePageTitle";

// Error messages for OAuth errors
const ERROR_MESSAGES: Record<string, string> = {
  oauth_denied: "You denied access to your Google account.",
  missing_code: "Invalid OAuth callback. Please try again.",
  invalid_state: "Invalid session state. Please try again.",
  email_not_verified: "Your Google email address is not verified.",
  no_invitation: "You don't have access to this application. Please contact an administrator.",
  invitation_expired: "Your invitation has expired. Please request a new invitation.",
  auth_failed: "Authentication failed. Please try again.",
};

export default function LoginPage() {
  usePageTitle("Login");
  const { isAuthenticated, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const error = searchParams.get("error");
  const errorMessage = error ? ERROR_MESSAGES[error] || "An error occurred during login." : null;

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/apps", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-bg dark:to-dark-bg-secondary flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Login card */}
        <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-lg dark:border dark:border-dark-border p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
              Shopify Dev OAuth
            </h1>
            <p className="text-gray-500 dark:text-dark-text-secondary text-sm mt-2">
              Sign in to continue
            </p>
          </div>

          {/* Error message */}
          {errorMessage && (
            <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300 text-center">{errorMessage}</p>
            </div>
          )}

          {/* Google sign in button */}
          <button
            onClick={login}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg shadow-sm bg-white dark:bg-dark-bg-tertiary hover:bg-gray-50 dark:hover:bg-dark-border focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-dark-bg-secondary transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className="text-gray-700 dark:text-dark-text font-medium">
              Sign in with Google
            </span>
          </button>

          {/* Divider */}
          <div className="my-6 border-t border-gray-100 dark:border-dark-border" />

          {/* Branding */}
          <div className="flex items-center justify-center">
            <Wordmark className="h-4 w-auto text-gray-300 dark:text-dark-text-muted" />
          </div>
        </div>
      </div>
    </div>
  );
}
